import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChartComponent, ApexOptions } from 'ng-apexcharts';
import { EventService } from '@reenstudio/app/core/services/event-service';
import { NGXLogger } from 'ngx-logger';
import { consumiChartObj } from '@reenstudio/app/core/models//charts/consumi-model';
import { ConsumiChartService } from '@reenstudio/app/core/services/charts/consumi-service';
import { Subscription, of, switchMap } from 'rxjs';
import { UserRole } from '@reenstudio/app/core/enum/user-role';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { PeriodChartApiService } from '@reenstudio/app/core/services/charts-api/period-api-service';
import { SiteService } from '@reenstudio/app/core/services/site-service';

@Component({
  selector: 'app-consumi',
  templateUrl: './consumi.component.html',
  styleUrls: ['./consumi.component.scss'],
})
export class ConsumiComponent implements OnInit {
  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ApexOptions>;

  @ViewChild('chartContainer') chartContainer!: ElementRef; // Per scroll bar grafico
  scrollValue: number = 0;

  subscription: Subscription;
  siteSubscription: Subscription;

  asyncOperations: Promise<any>[] = [];

  chartObj: consumiChartObj = {
    startPeriod: 1, // Default 1 giorno prima
    labelDate: [],
    overwriteLabelDate: [],
    legend: false,
    type: 'days',
    end: new Date(),
    start: new Date(),
    year: 0,
    month: 0,
    selectedSite: 'total',
  };

  loading: boolean = false;
  ignoreTime: boolean = false;
  siteName: string;

  userLevel: UserRole;

  constructor(
    private apiService: PeriodChartApiService,
    private logger: NGXLogger,
    private chartService: ConsumiChartService,
    private siteService: SiteService,
    private storageService: StorageService
  ) {
    this.chartObj.start = new Date(
      this.chartObj.end.getTime() -
        this.chartObj.startPeriod * 24 * 60 * 60 * 1000
    );

    this.chartOptions = this.chartService.setConsumiChart(this.chartObj);
  }

  ngOnInit(): void {
    EventService.pageFilters.subscribe((data) => {
      this.logger.debug('Consumi component filter event data:', data);
      this.chartObj.type = data.periodType;

      if (this.chartObj.type === 'days') {
        this.chartObj.startPeriod =
          data.periodValue > 1
            ? Number.parseInt(data.periodValue) - 1
            : Number.parseInt(data.periodValue);
        this.chartObj.end = new Date();
        this.chartObj.start = new Date(
          this.chartObj.end.getTime() -
            this.chartObj.startPeriod * 24 * 60 * 60 * 1000
        );
      } else if (this.chartObj.type === 'custom') {
        this.chartObj.start = data.customStartDate;
        this.chartObj.end = data.customEndDate;

        const differenzaInMillisecondi =
          this.chartObj.end.getTime() - this.chartObj.start.getTime();
        // Conteggio giorni per settare a true ingoreTime
        if (differenzaInMillisecondi / (1000 * 60 * 60 * 24) > 1) {
          this.chartObj.startPeriod = Number.parseInt(
            (differenzaInMillisecondi / (1000 * 60 * 60 * 24)).toFixed(0)
          );
        } else {
          this.chartObj.startPeriod = 1;
        }
      } else if (this.chartObj.type === 'months') {
        this.chartObj.year = data.yearsList;
        this.chartObj.month = data.monthList - 1;

        const lastDayOfMonth = this.chartService.getTotalDaysInMonth(
          this.chartObj
        );

        this.chartObj.start = new Date(
          this.chartObj.year,
          this.chartObj.month,
          1
        );
        this.chartObj.end = new Date(
          this.chartObj.year,
          this.chartObj.month,
          lastDayOfMonth
        );
      }

      // Se il livello è site, il valore è quello salvato sullo storage
      if (this.storageService.getLevel() === UserRole.SITE) {
        this.siteService
          .setSelectedSite(data.site)
          .subscribe((selectedSite: any) => {
            this.chartObj.selectedSite = selectedSite.id;
            this.siteName = selectedSite.description;
          });
      } else {
        const siteDetails = this.siteService.getStorageSite(data.site);

        if (siteDetails) {
          this.chartObj.selectedSite = siteDetails.id;
          this.siteName = siteDetails.description;
        }
      }

      this.getData(this.chartObj.selectedSite);
    });

    if (this.storageService.getLevel() === UserRole.SITE) {
      this.siteService.setSelectedSite().subscribe((selectedSite: any) => {
        this.chartObj.selectedSite = selectedSite.id;
        this.siteName = selectedSite.name;
      });
    }

    this.getData(this.chartObj.selectedSite);

    EventService.changeCer.subscribe(() => {
      const cer = this.storageService.getCer();
      if (cer) {
        this.getData(cer);
      }
    });

    this.siteSubscription = EventService.changeSite.subscribe(() => {
      this.getData(this.chartObj.selectedSite);
    });
  }

  getData(site: number | string) {
    console.log('SITE SITE', site);
    this.scrollValue = 0;
    this.loading = true;

    if (this.chartObj.startPeriod > 1) {
      this.ignoreTime = true;
    } else {
      this.ignoreTime = false;
    }

    let selectedSite: string | number = 'total';
    if (typeof site !== 'string') {
      const siteId = this.storageService.getSite();
      if (siteId) {
        selectedSite = siteId;
      }
    } else {
      selectedSite = 'total';
    }

    let cer = this.storageService.getCer();
    this.subscription = this.apiService
      .getPeriod(
        cer,
        selectedSite,
        this.chartObj.start,
        this.chartObj.end,
        this.ignoreTime
      )
      .pipe(
        switchMap((apiData) => {
          this.logger.debug('Consumi API received data', apiData);

          this.logger.debug('Consumi chartObj data', this.chartObj);

          // Esegui i calcoli necessari
          const res = this.chartService.getCosnumiData(this.chartObj, apiData);

          this.chartObj = res;

          // Ritorna un Observable di completamento
          return of(null);
        })
      )
      .subscribe(() => {
        this.chartOptions = this.chartService.setConsumiChart(this.chartObj);
        this.logger.debug('Consumi chartOptions updated', this.chartOptions);
        this.loading = false;
      });
  }

  // toggleBar(prefix: string) {
  //   this.chart.series.forEach(function (serie) {
  //     if (
  //       typeof serie === 'object' &&
  //       serie.name &&
  //       serie.name.startsWith(prefix)
  //     ) {
  //     if (serie.name.startsWith(prefix)) {
  //       this.chart.toggleSeries(serie.name);
  //     }
  //   });
  // }

  // toggleSeries(prefix: string): void {
  //   this.chart.series.forEach((serie) => {
  //     if (
  //       typeof serie === 'object' &&
  //       serie.name &&
  //       serie.name.startsWith(prefix)
  //     ) {
  //       this.hideSeries(serie.name);
  //     }
  //   });
  // }

  // hideSeries(name: string): void {
  //   console.log(name);
  //   this.chart.toggleSeries(name);
  // }

  onSliderChange(event: Event) {
    const sliderValue = (event.target as HTMLInputElement).valueAsNumber;

    const parentContainer = this.chartContainer.nativeElement as HTMLElement;

    let chartWidth = 5000; // Ottieni la larghezza del grafico

    if (this.chartObj.startPeriod > 7) {
      chartWidth = 12000; // Ottieni la larghezza del grafico
    } else {
      chartWidth = 5000; // Ottieni la larghezza del grafico
    }

    const visibleWidth = parentContainer.clientWidth; // Ottieni la larghezza visibile del container padre
    const maxScroll = chartWidth - visibleWidth; // Calcola la massima quantità di scorrimento disponibile
    const scrollAmount = (maxScroll / 100) * sliderValue;

    this.chart.updateOptions({
      chart: {
        offsetX: -scrollAmount,
      },
    });

    parentContainer.scrollLeft = scrollAmount;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.siteSubscription.unsubscribe();
  }
}
